import React, { useCallback, useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import DailyIframe from '@daily-co/daily-js';
import { Button } from './ui/button';
import { Mic, MicOff, Video, VideoOff, PhoneOff, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';

const VideoCall = ({ 
  roomUrl, 
  token, 
  userName,
  onCallEnd,
  onError 
}) => {
  const [loading, setLoading] = useState(true);
  const [joined, setJoined] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [participantCount, setParticipantCount] = useState(0);
  const [error, setError] = useState(null);
  
  const containerRef = useRef(null);
  const frameRef = useRef(null);
  const mountedRef = useRef(true);
  const initDoneRef = useRef(false);

  const cleanupFrame = useCallback(() => {
    if (frameRef.current) {
      try { frameRef.current.destroy(); } catch (e) { /* ignore */ }
      frameRef.current = null;
    }
    try {
      const existing = DailyIframe.getCallInstance();
      if (existing) existing.destroy();
    } catch (e) { /* ignore */ }
  }, []);

  const leaveCall = useCallback(() => {
    mountedRef.current = false;
    cleanupFrame();
    if (onCallEnd) onCallEnd();
  }, [onCallEnd, cleanupFrame]);

  useEffect(() => {
    if (initDoneRef.current) return;
    initDoneRef.current = true;
    mountedRef.current = true;

    const initCall = async () => {
      if (!roomUrl || !containerRef.current) return;

      cleanupFrame();
      if (containerRef.current) containerRef.current.innerHTML = '';
      await new Promise(resolve => setTimeout(resolve, 300));

      try {
        const frame = DailyIframe.createFrame(containerRef.current, {
          iframeStyle: {
            width: '100%',
            height: '100%',
            border: '0',
            borderRadius: '8px',
          },
          showLeaveButton: false,
          showFullscreenButton: true,
        });

        frameRef.current = frame;

        frame.on('joined-meeting', () => {
          if (mountedRef.current) { setJoined(true); setLoading(false); }
        });
        frame.on('participant-joined', (event) => {
          if (mountedRef.current) {
            setParticipantCount(prev => prev + 1);
            if (event.participant?.user_name) toast.info(`${event.participant.user_name} joined`);
          }
        });
        frame.on('participant-left', () => {
          if (mountedRef.current) setParticipantCount(prev => Math.max(0, prev - 1));
        });
        frame.on('left-meeting', () => { if (mountedRef.current) leaveCall(); });
        frame.on('error', (err) => {
          console.error('[VideoCall] Daily error:', err);
          if (mountedRef.current) {
            setError(err?.errorMsg || err?.error?.msg || 'Connection error');
            setLoading(false);
            if (onError) onError(err);
          }
        });

        await frame.join({ url: roomUrl, token: token, userName: userName || 'User' });
        setTimeout(() => { if (mountedRef.current) setLoading(false); }, 10000);

      } catch (err) {
        console.error('[VideoCall] Init error:', err);
        if (mountedRef.current) {
          setError(err.message || 'Failed to connect to video call');
          setLoading(false);
          if (onError) onError(err);
        }
      }
    };

    initCall();
    return () => { mountedRef.current = false; cleanupFrame(); };
  }, []);

  useEffect(() => {
    if (frameRef.current && joined) {
      try { frameRef.current.setLocalAudio(audioEnabled); } catch (e) { /* ignore */ }
    }
  }, [audioEnabled, joined]);

  useEffect(() => {
    if (frameRef.current && joined) {
      try { frameRef.current.setLocalVideo(videoEnabled); } catch (e) { /* ignore */ }
    }
  }, [videoEnabled, joined]);

  const portalContainer = document.getElementById('root');

  return createPortal(
    <div 
      className="fixed inset-0 z-[9999] flex flex-col bg-black"
      data-testid="video-call-overlay"
    >
      {/* Compact header bar */}
      <div className="flex items-center justify-between px-3 py-2 bg-gray-900 border-b border-gray-700 flex-shrink-0">
        <div className="flex items-center gap-2 text-white">
          <Video className="w-4 h-4 text-green-400 flex-shrink-0" />
          <span className="font-medium text-sm">Video Call</span>
          {joined && (
            <span className="text-xs text-green-400">
              Connected ({participantCount + 1})
            </span>
          )}
        </div>
        <button 
          onClick={leaveCall}
          className="p-1.5 hover:bg-gray-700 rounded-full transition-colors"
          data-testid="video-call-close"
        >
          <X className="w-5 h-5 text-gray-300" />
        </button>
      </div>

      {/* Video container — takes ALL remaining space */}
      <div className="flex-1 relative min-h-0">
        <div 
          ref={containerRef}
          className="absolute inset-0"
        />
        {loading && !error && !joined && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-900/70 z-10 pointer-events-none">
            <div className="text-center text-white">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
              <p className="text-sm">Connecting to video call...</p>
            </div>
          </div>
        )}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-900 z-10">
            <div className="text-center text-white px-4">
              <p className="text-red-400 mb-2 font-medium">Failed to connect</p>
              <p className="text-sm text-gray-400 mb-4 max-w-sm">{error}</p>
              <Button variant="outline" size="sm" onClick={leaveCall}>Close</Button>
            </div>
          </div>
        )}
      </div>

      {/* Compact bottom controls */}
      <div className="flex items-center justify-center gap-4 py-2 bg-gray-900 border-t border-gray-700 flex-shrink-0">
        <Button
          variant={audioEnabled ? "outline" : "destructive"}
          size="icon"
          onClick={() => setAudioEnabled(!audioEnabled)}
          disabled={!joined}
          className="w-10 h-10 rounded-full border-gray-600"
          data-testid="video-call-mic-toggle"
        >
          {audioEnabled ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />}
        </Button>
        
        <Button
          variant={videoEnabled ? "outline" : "destructive"}
          size="icon"
          onClick={() => setVideoEnabled(!videoEnabled)}
          disabled={!joined}
          className="w-10 h-10 rounded-full border-gray-600"
          data-testid="video-call-video-toggle"
        >
          {videoEnabled ? <Video className="w-4 h-4" /> : <VideoOff className="w-4 h-4" />}
        </Button>
        
        <Button
          variant="destructive"
          size="icon"
          onClick={leaveCall}
          className="w-10 h-10 rounded-full"
          data-testid="video-call-end"
        >
          <PhoneOff className="w-4 h-4" />
        </Button>
      </div>
    </div>,
    portalContainer
  );
};

export default VideoCall;
